import * as React from "react";
import { createPortal } from "react-dom";
import { useLingui } from "@lingui/react/macro";
import { CheckIcon, ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { cn } from "@app/ui/lib/Utils";
import {
  dismissDownload,
  useDownloads,
  type DownloadRecord,
} from "@app/ui/lib/downloadStore";

/** 원형 진행 링. progress=null 이면 불확정(회전) 스피너. */
function ProgressRing({
  progress,
  status,
}: {
  progress: number | null;
  status: DownloadRecord["status"];
}) {
  const size = 32;
  const stroke = 3;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  // 불확정이면 링의 25% 만 그려 회전시킨다. 확정이면 progress 만큼 채운다.
  const fraction = progress ?? 0.25;
  const dashoffset = circumference * (1 - fraction);
  const indeterminate = progress === null && status === "active";

  return (
    <span className="relative flex shrink-0 items-center justify-center" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        className={cn("-rotate-90", indeterminate && "animate-spin")}
        viewBox={`0 0 ${size} ${size}`}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-border"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashoffset}
          className={cn(
            "transition-[stroke-dashoffset] duration-200",
            status === "error" ? "text-destructive" : "text-primary",
          )}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center">
        {status === "done" ? (
          <CheckIcon className="size-4 text-primary" />
        ) : status === "error" ? (
          <ExclamationTriangleIcon className="size-4 text-destructive" />
        ) : progress !== null ? (
          <span className="text-[9px] font-medium tabular-nums text-muted-foreground">
            {Math.round(progress * 100)}
          </span>
        ) : null}
      </span>
    </span>
  );
}

function DownloadTrayItem({ record }: { record: DownloadRecord }) {
  const { t } = useLingui();
  return (
    <div className="pointer-events-auto flex items-center gap-3 rounded-lg border border-border bg-popover px-3 py-2 shadow-md">
      <ProgressRing progress={record.progress} status={record.status} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{record.fileName}</p>
        <p className="text-xs text-muted-foreground">
          {record.status === "error"
            ? t`다운로드 실패`
            : record.status === "done"
              ? t`다운로드 완료`
              : t`다운로드 중…`}
        </p>
      </div>
      {record.status !== "active" && (
        <button
          type="button"
          aria-label={t`닫기`}
          onClick={() => dismissDownload(record.id)}
          className="flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <XMarkIcon className="size-4" />
        </button>
      )}
    </div>
  );
}

/**
 * 앱 트리 어디에나 한 번 마운트해두면 진행 중인 다운로드를 우하단에 원형 진행 링으로
 * 보여준다(Toaster 와 같은 방식). 상태는 모듈 스코프 스토어(downloadStore)에 있어
 * 화면을 이동해도 유지된다.
 */
export function DownloadTray() {
  const downloads = useDownloads();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || downloads.length === 0) return null;

  return createPortal(
    <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-xs flex-col gap-2">
      {downloads.map((record) => (
        <DownloadTrayItem key={record.id} record={record} />
      ))}
    </div>,
    document.body,
  );
}
